import jsPDF from 'jspdf';

const YOUNGEVITY_URL = 'https://doduro.youngevity.com/ca_en/healthy-body-start-pak-trade-2-5.html';

// Colors (match app palette)
const C = {
  ink: [26, 36, 33],
  soft: [74, 90, 83],
  moss: [126, 153, 121],
  green: [74, 124, 89],
  red: [192, 87, 70],
  border: [229, 225, 216],
};

const setColor = (doc, rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
const setDraw = (doc, rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

export const downloadReportPdf = (data) => {
  const r = data.report || {};
  const intake = data.intake || {};
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const maxW = pageW - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
  };

  const drawH1 = (text) => {
    ensureSpace(46);
    doc.setFont('times', 'normal');
    doc.setFontSize(28);
    setColor(doc, C.ink);
    const lines = doc.splitTextToSize(text, maxW);
    doc.text(lines, margin, y);
    y += lines.length * 30 + 8;
  };

  const drawH2 = (text, accent = C.green) => {
    ensureSpace(38);
    y += 6;
    doc.setFont('times', 'bold');
    doc.setFontSize(16);
    setColor(doc, accent);
    doc.text(text, margin, y);
    y += 8;
    setDraw(doc, C.border);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageW - margin, y);
    y += 14;
  };

  const drawLabel = (text) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(doc, C.moss);
    doc.text(text.toUpperCase(), margin, y);
    y += 12;
  };

  const drawParagraph = (text, size = 11, color = C.soft) => {
    if (!text) return;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    setColor(doc, color);
    const lines = doc.splitTextToSize(text, maxW);
    ensureSpace(lines.length * (size + 3));
    doc.text(lines, margin, y);
    y += lines.length * (size + 3) + 6;
  };

  const drawList = (items, keys = { name: 'name', sub: 'sub', why: 'why' }) => {
    (items || []).forEach((item) => {
      const name = item[keys.name];
      const sub = item[keys.sub];
      const why = item[keys.why];
      ensureSpace(40);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      setColor(doc, C.ink);
      const nameLines = doc.splitTextToSize(name || '', maxW - 120);
      doc.text(nameLines, margin, y);
      if (sub) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        setColor(doc, C.moss);
        doc.text(String(sub), pageW - margin, y, { align: 'right' });
      }
      y += nameLines.length * 13 + 2;
      if (why) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        setColor(doc, C.soft);
        const whyLines = doc.splitTextToSize(String(why), maxW);
        ensureSpace(whyLines.length * 12 + 6);
        doc.text(whyLines, margin, y);
        y += whyLines.length * 12;
      }
      y += 8;
    });
  };

  // Header brand
  doc.setFont('times', 'italic');
  doc.setFontSize(11);
  setColor(doc, C.green);
  doc.text('Wellth  ·  Health is the greatest wealth', margin, y);
  y += 22;

  drawH1('Your personalized plan');

  // Intake summary
  const created = new Date(data.created_at || Date.now()).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  drawParagraph(`${created}  ·  Age ${intake.age}  ·  ${intake.height}  ·  ${intake.weight_lbs} lbs`, 10, C.moss);
  if (intake.health_issues?.length) drawParagraph(`Focus: ${intake.health_issues.join(', ')}`, 10, C.moss);

  y += 6;
  drawParagraph(r.greeting || '', 12, C.ink);

  drawH2('Foods that heal you', C.moss);
  drawList(r.good_foods);

  drawH2('Foods to avoid', C.red);
  drawList(r.bad_foods);

  drawH2('Your supplement stack', C.green);
  drawList(r.supplements, { name: 'name', sub: 'dosage', why: 'why' });
  drawLabel('Shop supplements');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setColor(doc, C.green);
  doc.textWithLink('90 For Life at Youngevity  →', margin, y, { url: YOUNGEVITY_URL });
  y += 20;

  drawH2('At-home workouts', C.green);
  drawList(r.home_exercises, { name: 'name', sub: 'sets_reps', why: 'why' });

  drawH2('At the gym', C.green);
  drawList(r.gym_exercises, { name: 'name', sub: 'sets_reps', why: 'why' });

  drawH2('Stretching & mobility', C.moss);
  drawList(r.stretching_mobility, { name: 'name', sub: 'duration', why: 'why' });

  drawH2('The household principle', C.moss);
  drawParagraph(r.household_tip || '', 11, C.ink);

  y += 10;
  ensureSpace(60);
  doc.setFont('times', 'italic');
  doc.setFontSize(14);
  setColor(doc, C.ink);
  const closing = doc.splitTextToSize(`"${r.closing_encouragement || ''}"`, maxW);
  doc.text(closing, margin, y);
  y += closing.length * 18 + 12;

  // Footer disclaimer on last page
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  setColor(doc, C.soft);
  doc.text('Wellth is educational and not medical advice. Consult your physician for medical concerns.', margin, pageH - 24);

  const filename = `wellth-plan-${new Date(data.created_at || Date.now()).toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
};
